import BigNumber from 'bignumber.js'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'

// const subscriptNumber = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉']
BigNumber.config({
  DECIMAL_PLACES: 20,
})
// eslint-disable-next-line max-params
export function humanFormatNumber(
  num: number | undefined,
  fixed = 2,
  withPrefixSpace = false,
  withPrefixText = '',
): string {
  if (num === undefined) {
    return '-'
  }
  const absNum = Math.abs(num)
  const sign = num < 0 ? '-' : ''
  const bn = new BigNumber(num.toString())
  if (absNum === 0) {
    return num.toFixed(2)
  }
  if (absNum < 1 && absNum > 0) {
    return num.toFixed(fixed + 3)
  }
  if (bn.gte(new BigNumber('10000000000000000000'))) {
    return bn.toPrecision(4)
  }
  const units = [withPrefixSpace ? '' : '', 'K', 'M', 'B', 'T', 'Q']
  const unitSize = 1000

  const unitIndex = Math.min(Math.floor(Math.log10(absNum) / Math.log10(unitSize)), units.length - 1)
  const scaledNum = absNum / unitSize ** unitIndex
  return `${sign}${withPrefixText}${scaledNum.toFixed(fixed)}${units[unitIndex]}`
}

// eslint-disable-next-line max-params
export function formatAmount(balance?: any, decimals = 0, fixed?: number, rawString?: boolean): string {
  // 处理无效输入
  if (balance === undefined || balance === null || balance === '')
    return '-'

  const bn = new BigNumber(balance.toString())
  if (bn.isNaN() || !bn.isFinite())
    return '-'

  const displayBalance = bn.div(new BigNumber(10).pow(decimals))
  if (displayBalance.gte(new BigNumber('10000000000000000000'))) {
    return displayBalance.toPrecision(4)
  }
  if (displayBalance.abs().lt(1000)) {
    if (displayBalance.eq(0)) {
      fixed ??= 2
    }
    if (displayBalance.abs().lt(1)) {
      fixed ??= 6
    }
    fixed ??= 2
    const result = displayBalance.toFixed(fixed, BigNumber.ROUND_FLOOR)
    return result === '0.000000' ? '0.00' : result
  }

  if (displayBalance.abs().gte(1000000)) {
    fixed ??= 0
  }

  fixed ??= 2
  if (rawString) {
    return displayBalance.toFixed(fixed, BigNumber.ROUND_FLOOR)
  }
  return displayBalance.toNumber().toLocaleString(undefined, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  })
}

export function formatUsdAmount(balance?: any, _fixed?: number): string {
  if (balance === undefined || balance === null || balance === '')
    return '-'

  const displayBalance = new BigNumber(balance.toString())
  if (displayBalance.isNaN() || !displayBalance.isFinite()) {
    return '-'
  }
  const mFixed = _fixed ?? 2
  const fixed = displayBalance.abs().lt(1_000_000) ? mFixed : 0
  if (displayBalance.gte(new BigNumber('10000000000000000000'))) {
    return displayBalance.toPrecision(4)
  }
  return displayBalance.toNumber().toLocaleString(undefined, {
    minimumFractionDigits: fixed,
    maximumFractionDigits: fixed,
  })
}

export function formatTime(time?: string | number | Date, format = 'YYYY-MM-DD HH:mm:ss'): string {
  if (!time) {
    return '-'
  }
  return dayjs(time).format(format)
}
export function removeSuffixZero(string: string): string {
  return string.replace(/\.?0+$/, '')
}
export function formatPrice(price?: string | number, fixed?: number): string {
  // 处理无效输入
  if (price === undefined || price === null || price === '')
    return '-'

  try {
    // 如果是数字，先控制精度
    if (typeof price === 'number')
      price = price.toFixed(20)
    const bn = new BigNumber(price)
    if (bn.isNaN() || bn.isZero())
      return '0'

    const abs = bn.abs()

    // 处理大于等于 1 的价格
    if (abs.gte(1))
      return abs.toFixed(2)

    // 处理小于 1 的价格
    if (abs.lt(0.0001)) {
      const str = abs.toFixed(20)
      const match = str.match(/^0\.(0*)[1-9]/)
      if (match) {
        const zerosCount = match[1].length
        const significantPart = str.slice(match[0].length - 1, match[0].length + 3)
        // const subscriptZeros = zerosCount.toString().split('').map(digit => subscriptNumber[Number.parseInt(digit)]).join('')
        return `0.0{${zerosCount}}${significantPart}`
      }
    }

    // 对于一般小数（0.00001 到 1 之间），保留 6 位小数
    return abs.toFixed(fixed ?? 6)
  }
  catch (error) {
    console.error('Price format error:', error)
    return '0'
  }
}
export function formatNumberCompress(number?: string | number, fixed?: number): string {
  // 处理无效输入
  if (number === undefined || number === null || number === '')
    return '-'

  try {
    // 如果是数字，先控制精度
    if (typeof number === 'number')
      number = number.toFixed(20)
    const bn = new BigNumber(number)
    if (bn.isNaN() || bn.isZero())
      return '0'

    const abs = bn.abs()

    // 处理大于等于 1 的价格
    if (abs.gte(1))
      return abs.toFixed(2)

    // 处理小于 1 的价格
    if (abs.lt(0.0001)) {
      const str = abs.toFixed(20)
      const match = str.match(/^0\.(0*)[1-9]/)
      if (match) {
        const zerosCount = match[1].length
        const significantPart = str.slice(match[0].length - 1, match[0].length)
        // const subscriptZeros = zerosCount.toString().split('').map(digit => subscriptNumber[Number.parseInt(digit)]).join('')
        return removeSuffixZero(`0.0{${zerosCount}}${significantPart}`)
      }
    }

    // 对于一般小数（0.00001 到 1 之间），保留 6 位小数
    return abs.toFixed(fixed ?? 4)
  }
  catch (error) {
    console.error('Price format error:', error)
    return '0'
  }
}
export function formatPnl(number: number | undefined): number {
  return number ?? 0
}

interface FormatPercentOptions {
  prefix?: boolean
  precision?: number
  defaultValue?: string
  max?: number
  largerSign?: boolean
}

export function formatPercent(number: number | undefined, options: FormatPercentOptions = {}): string {
  const { prefix = true, precision = 2, defaultValue = '--%', max = 99.999, largerSign = true } = options

  if (number === undefined) {
    return defaultValue
  }
  if (Number.isNaN(number)) {
    return '0%'
  }
  if (number === 0) {
    return '0%'
  }
  const overflow = Math.abs(number) > max
  const _percent = overflow ? max : number
  const value = (_percent * 100).toFixed(precision)
  const sign = prefix && _percent > 0 ? '+' : ''
  const largerSignStr = overflow && largerSign ? '>' : ''
  return `${sign}${largerSignStr}${value}%`
}
export function getTransactionTypeText(
  t: ReturnType<typeof useTranslations>,
  type: string,
) {
  switch (type) {
    case '2':
    case '4': {
      return t('trade.transaction.limit')
    }
    case '1': {
      return t('trade.transaction.market')
    }
    case '3': {
      return t('trade.transaction.one_click')
    }

    case '5': {
      return t('trade.transaction.moving_stop_loss')
    }
    default: {
      return type
    }
  }
}
export function covertNumber(value: string | number | undefined): number | undefined {
  if (value === undefined) {
    return undefined
  }
  const fmtNumber = +value
  if (Number.isNaN(fmtNumber)) {
    return undefined
  }
  return fmtNumber
}

// ========== Intl.NumberFormat Formatters ==========

/**
 * Check if signDisplay is supported in the current environment
 */
function isSignDisplaySupported(): boolean {
  try {
    const formatter = new Intl.NumberFormat('en-US', { signDisplay: 'always' } as any)
    return formatter.format(1).includes('+')
  }
  catch {
    return false
  }
}

/**
 * Polyfill for signDisplay when not natively supported
 */
function applySignDisplayPolyfill(
  formatted: string,
  value: number,
  signDisplay: 'auto' | 'never' | 'always' | 'exceptZero' | 'negative',
  isCurrency = false,
): string {
  // For currency, extract the currency symbol first
  let currencySymbol = ''
  let numberPart = formatted

  if (isCurrency) {
    // Match common currency symbols at the start or end
    const currencyMatch = formatted.match(/^([^0-9+-]+)|([^0-9+-]+)$/)
    if (currencyMatch) {
      currencySymbol = currencyMatch[0]
      numberPart = formatted.replace(currencySymbol, '').trim()
    }
  }

  switch (signDisplay) {
    case 'always': {
      if (value > 0 && !numberPart.startsWith('+')) {
        numberPart = `+${numberPart}`
      }
      break
    }

    case 'exceptZero': {
      if (value > 0 && !numberPart.startsWith('+')) {
        numberPart = `+${numberPart}`
      }
      if (value === 0) {
        numberPart = numberPart.replace(/^[+-]/, '')
      }
      break
    }

    case 'never': {
      numberPart = numberPart.replace(/^[+-]/, '')
      break
    }

    case 'negative':
    case 'auto':
    default: {
      // Keep as is
      break
    }
  }

  // Reconstruct with currency symbol
  if (isCurrency && currencySymbol) {
    // If currency was at the start, keep it at the start
    if (formatted.startsWith(currencySymbol)) {
      // Place sign before currency symbol
      const sign = numberPart.match(/^[+-]/)?.[0] || ''
      const cleanNumber = numberPart.replace(/^[+-]/, '')
      return `${sign}${currencySymbol}${cleanNumber}`
    }
    // If currency was at the end, keep it at the end
    return `${numberPart}${currencySymbol}`
  }

  return numberPart
}

/**
 * Create a safe NumberFormat with signDisplay polyfill
 */
function createSafeNumberFormat(
  locale: string,
  options: Intl.NumberFormatOptions & {
    signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero' | 'negative'
  },
): { format: (value: number) => string } {
  const { signDisplay, ...restOptions } = options
  const isSupported = isSignDisplaySupported()
  const isCurrency = options.style === 'currency'

  // If signDisplay is supported natively, use it
  if (isSupported && signDisplay) {
    return new Intl.NumberFormat(locale, { ...restOptions, signDisplay } as Intl.NumberFormatOptions)
  }

  // Otherwise, use polyfill
  const formatter = new Intl.NumberFormat(locale, restOptions)

  return {
    format: (value: number) => {
      const formatted = formatter.format(value)
      return signDisplay ? applySignDisplayPolyfill(formatted, value, signDisplay, isCurrency) : formatted
    },
  }
}

/**
 * Format currency with proper localization and display options
 */
export function formatCurrency(
  value: number,
  options: {
    currency?: string
    locale?: string
    currencyDisplay?: 'symbol' | 'code' | 'name' | 'narrowSymbol'
    notation?: 'standard' | 'scientific' | 'engineering' | 'compact'
    signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero' | 'negative'
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {},
): string {
  const {
    currency = 'USD',
    locale = 'en-US',
    currencyDisplay = 'symbol',
    notation = 'standard',
    signDisplay = 'auto',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options
  if (Number.isNaN(value)) {
    return '--'
  }
  const formatter = createSafeNumberFormat(locale, {
    style: 'currency',
    currency,
    currencyDisplay,
    notation,
    signDisplay,
    minimumFractionDigits,
    maximumFractionDigits,
  })

  return formatter.format(value)
}

/**
 * Format price with dynamic precision based on value
 */
export function formatPriceDisplay(
  price: number,
  options: {
    currency?: string
    locale?: string
    compact?: boolean
    signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero' | 'negative'
  } = {},
): string {
  const { currency = 'USD', locale = 'en-US', compact = false, signDisplay = 'auto' } = options

  // Dynamic precision based on price range
  let minimumFractionDigits = 2
  let maximumFractionDigits = 4

  if (price >= 1000) {
    minimumFractionDigits = 2
    maximumFractionDigits = 2
  }
  else if (price >= 1) {
    minimumFractionDigits = 2
    maximumFractionDigits = 4
  }
  else if (price >= 0.01) {
    minimumFractionDigits = 4
    maximumFractionDigits = 6
  }
  else {
    minimumFractionDigits = 6
    maximumFractionDigits = 8
  }

  const formatter = createSafeNumberFormat(locale, {
    style: 'currency',
    currency,
    notation: compact ? 'compact' : 'standard',
    signDisplay,
    minimumFractionDigits,
    maximumFractionDigits,
  })

  return formatter.format(price)
}

/**
 * Format percentage with consistent display
 */
export function formatPercentageDisplay(
  value: number,
  options: {
    locale?: string
    signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero' | 'negative'
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {},
): string {
  const {
    locale = 'en-US',
    signDisplay = 'always',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options

  const formatter = createSafeNumberFormat(locale, {
    style: 'percent',
    signDisplay,
    minimumFractionDigits,
    maximumFractionDigits,
  })

  const raw = formatter.format(value / 100)
  // Android JSC bug: some Intl percent outputs render '%' as a trailing '+'.
  // If a proper percent sign exists, use as-is. Otherwise, fix common bad cases.
  if (raw.includes('%')) {
    return raw
  }
  // e.g. "+25.00+"  -> "+25.00%"
  if (/\+$/.test(raw)) {
    return raw.replace(/\+$/, '%')
  }
  // Fallback: append a literal percent sign
  return `${raw}%`
}

/**
 * Format large numbers with compact notation (K, M, B, T)
 */
export function formatCompactNumber(
  value: number,
  options: {
    locale?: string
    currency?: string
    compactDisplay?: 'short' | 'long'
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {},
): string {
  const {
    locale = 'en-US',
    currency,
    compactDisplay = 'short',
    minimumFractionDigits = 0,
    maximumFractionDigits = 1,
  } = options

  const config: Intl.NumberFormatOptions = {
    notation: 'compact',
    compactDisplay,
    minimumFractionDigits,
    maximumFractionDigits,
  }

  if (currency) {
    config.style = 'currency'
    config.currency = currency
  }

  return new Intl.NumberFormat(locale, config).format(value)
}

/**
 * Format trading volume with appropriate units
 */
export function formatVolume(
  volume: number,
  options: {
    locale?: string
    currency?: string
    compact?: boolean
  } = {},
): string {
  const { locale = 'en-US', currency, compact = true } = options

  if (compact && volume >= 1000) {
    return formatCompactNumber(volume, { locale, currency })
  }

  const config: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }

  if (currency) {
    config.style = 'currency'
    config.currency = currency
  }

  return new Intl.NumberFormat(locale, config).format(volume)
}

/**
 * Format P&L (Profit & Loss) with appropriate sign display
 */
export function formatPnLDisplay(
  pnl: number,
  options: {
    locale?: string
    currency?: string
    compact?: boolean
    signDisplay?: 'auto' | 'never' | 'always' | 'exceptZero' | 'negative'
  } = {},
): string {
  const { locale = 'en-US', currency = 'USD', compact = false, signDisplay = 'exceptZero' } = options

  const formatter = createSafeNumberFormat(locale, {
    style: 'currency',
    currency,
    notation: compact ? 'compact' : 'standard',
    signDisplay,
    minimumFractionDigits: 2,
    maximumFractionDigits: compact ? 1 : 2,
  })

  const formatted = formatter.format(pnl)

  // Android JSC bug fix: ensure dollar sign is present
  // If the formatted string doesn't include a dollar sign, add it manually
  if (currency === 'USD' && !formatted.includes('$')) {
    const sign = formatted.match(/^[+-]/)?.[0] || ''
    const numberPart = formatted.replace(/^[+-]/, '')
    return `${sign}$${numberPart}`
  }

  return formatted
}

export function formatPnlColor(isZero: boolean, isPositive: boolean): string {
  if (isZero) {
    return 'text.t1/60'
  }
  return isPositive ? 'profit' : 'loss'
}

// Calculate real-time PnL percentage for positions
interface PnLCalculationParams {
  entryPrice: number | string
  currentPrice: number
  leverage: number | string
  isLong: boolean
  fallbackROE?: number | string
}

export function calculateRealTimePnlPercentage(params: PnLCalculationParams): number {
  const { entryPrice, currentPrice, leverage, isLong, fallbackROE } = params
  const entry = Number.parseFloat(String(entryPrice))
  const lev = Number.parseFloat(String(leverage || '1'))

  if (entry === 0 || currentPrice === 0) {
    // Fallback to original ROE if we can't calculate
    return Number.parseFloat(String(fallbackROE || '0')) * 100
  }

  // Calculate PnL percentage (ROE): price change percentage * leverage
  const priceChangePercentage = ((currentPrice - entry) / entry) * 100
  return priceChangePercentage * lev * (isLong ? 1 : -1)
}
export function formatPnlColorBg(isZero: boolean, isPositive: boolean): string {
  if (isZero) {
    return 'text.t1/60'
  }
  return isPositive ? 'profit' : 'loss'
}

/**
 * Format holding time in seconds to "xxd xxh" format
 */
export function formatHoldingTime(seconds: number): string {
  if (seconds <= 0) {
    return '0h'
  }

  const days = Math.floor(seconds / (24 * 60 * 60))
  const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60))

  if (days > 0 && hours > 0) {
    return `${days}d ${hours}h`
  }
  else if (days > 0) {
    return `${days}d`
  }
  return `${hours}h`
}

/**
 * Format wallet address to show first 6 and last 6 characters
 */
export function formatAddress(address: string, startLength = 6, endLength = 6): string {
  if (!address || address.length <= startLength + endLength) {
    return address
  }
  return `${address.slice(0, startLength)}...${address.slice(-endLength)}`
}
